/**
 * useRequisitionLifecycleSteps Hook
 *
 * Reads the automation settings of the requisition steps and turns them into the
 * step model both lifecycle layouts render.
 */
import { useQuery } from '@apollo/client/react';
import { ApprovalMode, DEFAULT_REQUISITION_COMPLETION } from '@insio/shared-types';
import {
  RequisitionApprovalSettingsDocument,
  RequisitionAutomationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { APPROVAL_MODE_LABEL_KEY, completionSummaryKeys, type LifecycleStep } from './types';

export function useRequisitionLifecycleSteps(): LifecycleStep[] {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const tClosure = useFeatureTranslation('systemSettings.automation.requisitionClosure');

  const { data: approvalData } = useQuery(RequisitionApprovalSettingsDocument);
  const { data: automationData } = useQuery(RequisitionAutomationSettingsDocument);

  // Defaults mirror the backend: both automations are on until somebody switches them off.
  const approvalMode = approvalData?.requisitionApprovalSettings.mode ?? ApprovalMode.AI_AND_USER;
  const isOrderingEnabled =
    automationData?.requisitionAutomationSettings.isAutoOrderEnabled ?? true;
  const completion = automationData?.requisitionAutomationSettings ?? {
    completionBaseStatus: DEFAULT_REQUISITION_COMPLETION.baseStatus,
    isDeliveryRequired: DEFAULT_REQUISITION_COMPLETION.isDeliveryRequired,
    isInvoicingRequired: DEFAULT_REQUISITION_COMPLETION.isInvoicingRequired
  };

  return [
    {
      key: 'validation',
      icon: 'list-ul',
      isLocked: true,
      stateLabel: t('alwaysOn')
    },
    {
      key: 'approval',
      icon: 'circle-check',
      isLocked: false,
      isEnabled: approvalMode !== ApprovalMode.SKIPPED,
      stateLabel: t(APPROVAL_MODE_LABEL_KEY[approvalMode])
    },
    {
      key: 'ordering',
      icon: 'bag-shopping',
      isLocked: false,
      isEnabled: isOrderingEnabled,
      stateLabel: isOrderingEnabled ? t('enabled') : t('disabled')
    },
    {
      key: 'closure',
      icon: 'box',
      isLocked: false,
      isEnabled: true,
      stateLabel: completionSummaryKeys(completion)
        .map(key => tClosure(key))
        .join(' + ')
    }
  ];
}
